import { Card } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { NextSeo } from 'next-seo'
import React, { useState } from 'react'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'

import { getWallets } from '../src/services/wallets'
import { LoadState } from '../types/general'
import { urls, tabs } from '../src/utils/config'
import { Wallet as IWallet } from '../types/wallet'

import Nav from '../src/components/Nav'
import Footer from '../src/components/Footer'

const WalletCard = dynamic(() => import('../src/components/Card'))
const SeoText = dynamic(() => import('../src/components/SeoText/Bitcoin'))
import Background from '../src/components/Subnav/Background'
const Description = dynamic(() => import('../src/components/Subnav/Description'))
const LoadingIndicator = dynamic(() => import('../src/components/Loading'))

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: '950px',
    margin: 'auto',
  },
  walletContainer: {
    marginBottom: '65px',
  },
  error: {
    padding: '50px',
    margin: '50px',
  },
}))

interface Props {
  wallets: IWallet[]
  initialLoadState: LoadState
}

function BitcoinWalletsContainer({ wallets, initialLoadState }: Props): JSX.Element {
  const classes = useStyles({})
  const [loadState] = useState<LoadState>(initialLoadState)
  const bitcoinWallets = wallets.filter(
    (obj) => obj.cryptocurrencies.find((obj2) => obj2.code === 'BTC') && obj.pure_currency,
  )

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.bitcoinWallets,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Best Bitcoin Wallets | Mobile, Web and Desktop Bitcoin Wallet Reviews by Cryptotesters`}
            description={`Bitcoin wallets are optimized to keep your private keys secure. They allow you to interact with the bitcoin network to send & receive transaction as well as to store your bitcoins.`}
            openGraph={{
              url: `https://cryptotesters.com${urls.bitcoinWallets}`,
              title: `Best Bitcoin Wallets | Mobile, Web and Desktop Bitcoin Wallet Reviews by Cryptotesters`,
              description: `Bitcoin wallets are optimized to keep your private keys secure. They allow you to interact with the bitcoin network to send & receive transaction as well as to store your bitcoins.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: 'https://cryptotesters.com/sharable_small.png',
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <Background />
            <Description selectedTab={tabs.bitcoinWallets} />
            {loadState === LoadState.ERROR && (
              <Card className={classes.error}>
                Sorry, there was a problem returning the wallets
              </Card>
            )}
            {loadState !== LoadState.LOADED ? (
              <LoadingIndicator color="secondary" />
            ) : (
              bitcoinWallets.map((obj: IWallet) => (
                <WalletCard key={obj._id} custodial={false} wallet={obj} />
              ))
            )}
            {loadState === LoadState.LOADED && bitcoinWallets.length === 0 && (
              <Card className={classes.error}>Sorry, we have nothing available to show you</Card>
            )}
            <SeoText />
          </div>
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let loadState = LoadState.LOADING
  let wallets: IWallet[] = []

  try {
    wallets = await getWallets()
    loadState = LoadState.LOADED
  } catch (e) {
    loadState = LoadState.ERROR
  }

  return {
    props: {
      initialLoadState: loadState,
      wallets,
    },
  }
}

export default BitcoinWalletsContainer
